import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Separator, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { FilterPickerSubject } from './productFiltersMeta';

type FilterPickerFooterProps = {
    /** Whose picker this is: the Clear button's test id follows it. */
    subject: FilterPickerSubject;
    /** How many values are picked, "No brand" / "Uncategorized" included. */
    count: number;
    onClear: () => void;
};

const CLEAR_TEST_ID: Record<FilterPickerSubject, string> = {
    brand: 'product-filters-brand-clear',
    category: 'product-filters-category-clear',
};

/**
 * The foot of a filter picker's popover (BOS-201): how many values are
 * picked, and Clear, which unpicks them all without closing the picker.
 */
export const FilterPickerFooter: React.FC<FilterPickerFooterProps> = ({
    subject,
    count,
    onClear,
}) => {
    const { t } = useTranslation('pages');

    return (
        <>
            <Separator />
            <div className="flex items-center justify-between gap-2 px-3 py-1.5">
                <Typography color="muted" type="body-xs">
                    {t(
                        'products.components.productTable.filters.selectedCount',
                        { count },
                    )}
                </Typography>
                <Button
                    isDisabled={count === 0}
                    onPress={onClear}
                    size="sm"
                    variant="ghost"
                    {...getNodeTestIdProp(CLEAR_TEST_ID[subject])}
                >
                    {t(
                        'products.components.productTable.filters.clearSelection',
                    )}
                </Button>
            </div>
        </>
    );
};
